import { useEffect, useMemo, useState } from "react";
import modalStyle from "../../styles/modal.module.scss";
import CollectionButton from "../CollectionButton";
import { Swiper, SwiperSlide } from "swiper/react";
import { closeIcon } from "../Icons";
import "swiper/css";
import "swiper/css/pagination";

export default function MobileMaterialModal({
  handleCloseModal,
  materialData,
  vendor,
  title,
  handleImageLoaded,
  imageLoaded,
  description,
  handleChangeImage,
  ROOT_SERVER,
  slug,
  loaded,
  imageNumber,
}) {
  const [imageIndex, setImageIndex] = useState(imageNumber || 0);
  const [isLandscape, setIsLandscape] = useState(false);

  const images = Array.isArray(materialData?.images) ? materialData.images : [];
  const character = Array.isArray(materialData?.character)
    ? materialData.character
    : [];
  const feature = Array.isArray(materialData?.feature)
    ? materialData.feature
    : [];

  const priceValue = useMemo(() => {
    const value = Number(materialData?.price);
    if (Number.isFinite(value)) {
      return value.toFixed(2);
    }
    return "--";
  }, [materialData?.price]);

  useEffect(() => {
    setImageIndex(imageNumber || 0);
  }, [imageNumber]);

  useEffect(() => {
    const updateOrientation = () => {
      if (typeof window === "undefined") {
        return;
      }

      setIsLandscape(window.matchMedia("(orientation: landscape)").matches);
    };

    updateOrientation();
    window.addEventListener("resize", updateOrientation);
    window.addEventListener("orientationchange", updateOrientation);

    return () => {
      window.removeEventListener("resize", updateOrientation);
      window.removeEventListener("orientationchange", updateOrientation);
    };
  }, []);

  const renderLandscape = () => (
    <div className={modalStyle.mobileLandscapeShell}>
      <div className={modalStyle.mobileLandscapeHeader}>
        <div className={modalStyle.mobileVendorBadge}>{vendor}</div>
        <button
          type="button"
          className={modalStyle.mobileCloseBar}
          onClick={handleCloseModal}
        >
          <span>Close</span>
          {closeIcon}
        </button>
      </div>

      <div className={modalStyle.mobileLandscapeImageWrap}>
        {!imageLoaded ? <div className="spinner" /> : null}

        <Swiper
          className={modalStyle.mobileSwiper}
          pagination={true}
          navigation={true}
          loop={false}
          onSwiper={(swiper) => swiper.activeIndex === imageIndex}
          onSlideChange={(swiper) => {
            handleChangeImage(swiper.activeIndex);
            setImageIndex(swiper.activeIndex);
          }}
        >
          {images.length >= 1
            ? images.map((item, i) => (
                <SwiperSlide
                  key={item + i}
                  className={modalStyle.mobileLandscapeSlide}
                >
                  <img
                    alt={item}
                    className={`${modalStyle.materialImage} ${modalStyle.mobileLandscapeImage}`}
                    src={`${ROOT_SERVER}${vendor}/${slug}/swatch/${item}`}
                    loading="lazy"
                    onLoad={handleImageLoaded}
                  />
                </SwiperSlide>
              ))
            : false}
        </Swiper>
      </div>

      <div className={modalStyle.mobileLandscapeTitleBar}>
        <h3 className={modalStyle.mobileTitle}>{title}</h3>
      </div>
    </div>
  );

  return (
    <div className={modalStyle.modalShade} onClick={handleCloseModal}>
      {loaded ? (
        <div
          className={`${modalStyle.materialModal} ${modalStyle.mobileModal}`}
          onClick={(e) => e.stopPropagation()}
        >
          {isLandscape ? (
            renderLandscape()
          ) : (
            <>
              <button
                type="button"
                className={modalStyle.mobileCloseBar}
                onClick={handleCloseModal}
              >
                <span>Close</span>
                {closeIcon}
              </button>

              <div
                className={`${modalStyle.modalContent} ${modalStyle.mobileContent}`}
              >
                {/* IMAGE CONTAINER START */}
                <div
                  className={`${modalStyle.imageContainer} ${modalStyle.mobileImageContainer}`}
                >
                  <div className={modalStyle.mobileHeaderRow}>
                    <div className={modalStyle.mobileVendorBadge}>{vendor}</div>
                    <div className={modalStyle.mobileHeaderActions}>
                      <div className={modalStyle.mobileCollectionAction}>
                        <CollectionButton materialId={materialData.id} />
                      </div>
                    </div>
                  </div>

                  {!imageLoaded ? <div className="spinner" /> : null}

                  <Swiper
                    className={modalStyle.mobileSwiper}
                    pagination={true}
                    navigation={true}
                    loop={false}
                    onSwiper={(swiper) => swiper.activeIndex === imageIndex}
                    onSlideChange={(swiper) => {
                      handleChangeImage(swiper.activeIndex);
                      setImageIndex(swiper.activeIndex);
                    }}
                  >
                    {images.length >= 1
                      ? images.map((item, i) => (
                          <SwiperSlide
                            key={item + i}
                            className={modalStyle.mobileSwiperSlide}
                          >
                            <img
                              alt={item}
                              className={modalStyle.materialImage}
                              src={`${ROOT_SERVER}${vendor}/${slug}/swatch/${item}`}
                              loading="lazy"
                              onLoad={handleImageLoaded}
                            />
                          </SwiperSlide>
                        ))
                      : false}
                  </Swiper>

                  <div className={modalStyle.mobileImageCounter}>
                    {images.length > 0
                      ? `${imageIndex + 1} / ${images.length}`
                      : "0 / 0"}
                  </div>

                  <div className={modalStyle.mobileSwatchRow}>
                    {images.map((item, i) => (
                      <div
                        key={item + i}
                        className={`${modalStyle.mobileSwatchDot} ${
                          imageIndex === i
                            ? modalStyle.mobileSwatchDotActive
                            : ""
                        }`}
                      ></div>
                    ))}
                  </div>
                </div>
                {/* IMAGE CONTAINER END */}
              </div>

              <div className={modalStyle.mobileDataWrap}>
                <h3 className={modalStyle.mobileTitle}>{title}</h3>
                <p className={modalStyle.mobileDescription}>{description}</p>

                <div className={modalStyle.mobilePriceLine}>
                  <span className={modalStyle.mobilePrice}>${priceValue}</span>
                  <span className={modalStyle.mobileUom}>/ sq.ft.</span>
                </div>

                <div className={modalStyle.mobileMetaGrid}>
                  <div className={modalStyle.mobileTag}>
                    Type: {materialData?.substrate || "--"}
                  </div>
                  <div className={modalStyle.mobileTag}>
                    Group: {materialData?.subtype || "--"}
                  </div>
                  <div className={modalStyle.mobileTag}>
                    Status: {materialData?.status || "--"}
                  </div>
                </div>

                <div className={modalStyle.mobileSection}>
                  <h5 className={modalStyle.mobileSectionTitle}>Character</h5>
                  <div className={modalStyle.mobileSectionBody}>
                    {character.length > 0 ? (
                      character.map((item, i) => (
                        <span key={item + i}>{item}</span>
                      ))
                    ) : (
                      <span>Not available</span>
                    )}
                  </div>
                </div>

                <div className={modalStyle.mobileSection}>
                  <h5 className={modalStyle.mobileSectionTitle}>Finish</h5>
                  <div className={modalStyle.mobileSectionBody}>
                    {feature.length > 0 ? (
                      feature.map((item, i) => (
                        <span key={item + i}>{item}</span>
                      ))
                    ) : (
                      <span>Not available</span>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div>Loading it up</div>
      )}
    </div>
  );
}
